import { useAuthStore } from '@/stores/auth-store';
import { useLocalUserAudit } from '@/hooks/use-local-user-audit';
import { AuthPage } from '@/pages/AuthPage';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  // F12.1 Path 2 / NC2: AuthGuard is now a pure gate. The auth listener
  // subscription lives in <AuthBootstrap /> (mounted above all routes in
  // App.tsx), so public routes like /invite/:token can also resolve auth state.
  // The store's initialize action must NOT be called from this component —
  // doing so would double-register onAuthStateChange whenever AuthGuard
  // remounts. The regression-lock test in AuthGuard.test.tsx greps this file
  // for the literal call shape `initialize()` and fails if it reappears.
  const status = useAuthStore((s) => s.status);

  // Run startup audit to detect any pre-M82 'local-user' sentinel rows still in Dexie.
  // Non-blocking — schedules via requestIdleCallback.
  useLocalUserAudit(status === 'authenticated');

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
          <p className="text-body-sm text-[var(--color-text-muted)]">Loading…</p>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <AuthPage />;
  }

  return <>{children}</>;
}
